import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  Search, Bell, Plus, TrendingUp, AlertTriangle, AlertCircle, Info,
  ChevronRight, Phone, Check, Users,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import "../styles/command-center.css";

/* ─────────────────────────────────────────────────────────────────────────────
   Command Center — the v2 dashboard.

   Every number on this page comes from the API. Where the prototype showed a
   metric the backend does not record, the tile shows the nearest real figure
   (see the labels) or an empty state — never a made-up value.
   Data refreshes every 30 seconds so the page can sit on a screen at the door.
───────────────────────────────────────────────────────────────────────────── */

const REFRESH_MS = 30000;
const VENUE_COLORS = ["c1", "c2", "c3", "c4", "c5"];
const MEAL_COLORS = ["#1b6595", "#22d474", "#f5a623", "#8b5cf6", "#3292cc", "#00b4d8", "#ff4d4d"];

/* ── helpers ─────────────────────────────────────────────────────────────── */
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const pct = (a, b) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);

function initials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || parts[0][1] || "")).toUpperCase();
}

function hhmm(t) {
  if (!t) return "";
  const m = String(t).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : String(t);
}

function toDate(v) {
  if (!v || typeof v === "boolean") return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function ago(d) {
  if (!d) return "";
  const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 60) return "now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function checkinTime(a) {
  return toDate(a?.checked_in_at) || toDate(a?.check_in_time) || toDate(a?.checked_in);
}

/** Cumulative check-ins across the day as an SVG path in a 100×44 box. */
function sparkPath(times) {
  if (!times.length) return { line: "M0,40 L100,40", area: "M0,40 L100,40 L100,44 L0,44 Z", end: [100, 40] };
  const sorted = [...times].sort((a, b) => a - b);
  const start = sorted[0] - 15 * 60 * 1000;
  const end = Math.max(Date.now(), sorted[sorted.length - 1]);
  const span = Math.max(end - start, 60 * 1000);
  const steps = 12;
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const t = start + (span * i) / steps;
    const count = sorted.filter((x) => x <= t).length;
    const x = (100 * i) / steps;
    const y = 40 - (36 * count) / sorted.length;
    pts.push([Number(x.toFixed(2)), Number(y.toFixed(2))]);
  }
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0]},${p[1]}`).join(" ");
  return { line, area: `${line} L100,44 L0,44 Z`, end: pts[pts.length - 1] };
}

async function safe(promise, fallback) {
  try {
    const res = await promise;
    return res?.data ?? fallback;
  } catch {
    return fallback;
  }
}

/* ── Top bar ─────────────────────────────────────────────────────────────── */
function TopBar({ locationLabel, venueCount, unread, canCreate }) {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  const runSearch = useCallback(async (value) => {
    if (!value || value.trim().length < 2) {
      setResults(null);
      setOpen(false);
      return;
    }
    const data = await safe(authAxios().get("/search", { params: { q: value.trim() } }), null);
    setResults(data || { events: [], vendors: [], users: [] });
    setOpen(true);
  }, [authAxios]);

  const onChange = (value) => {
    setQ(value);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => runSearch(value), 300);
  };

  const go = (path) => {
    setOpen(false);
    setQ("");
    navigate(path);
  };

  const total = results
    ? (results.events?.length || 0) + (results.vendors?.length || 0) + (results.users?.length || 0)
    : 0;
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

  return (
    <header className="ivcc-topbar" data-testid="cc-topbar">
      <div className="ivcc-datebox">
        <div className="ivcc-date">{format(new Date(), "EEE, MMM d")}</div>
        <div className="ivcc-loc">{locationLabel}</div>
      </div>

      <div className="ivcc-search" ref={boxRef}>
        <Search strokeWidth={2} />
        <input
          ref={inputRef}
          id="cc-search"
          type="text"
          value={q}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => results && setOpen(true)}
          placeholder="Search events, vendors, people…"
          aria-label="Search"
          data-testid="cc-search"
        />
        <kbd>{isMac ? "⌘K" : "Ctrl K"}</kbd>
        {open && results && (
          <div className="ivcc-results" role="listbox">
            {total === 0 && <div className="ivcc-results-empty">Nothing matches “{q}”.</div>}
            {results.events?.length > 0 && (
              <>
                <div className="ivcc-results-h">Events</div>
                {results.events.map((e) => (
                  <button key={e.event_id} type="button" className="ivcc-result" onClick={() => go(`/events/${e.event_id}`)}>
                    <div className="ivcc-result-t">{e.title}</div>
                    <div className="ivcc-result-s">{[e.location, e.start_date].filter(Boolean).join(" · ")}</div>
                  </button>
                ))}
              </>
            )}
            {results.vendors?.length > 0 && (
              <>
                <div className="ivcc-results-h">Vendors</div>
                {results.vendors.map((v) => (
                  <button key={v.vendor_id} type="button" className="ivcc-result" onClick={() => go("/vendors")}>
                    <div className="ivcc-result-t">{v.name}</div>
                    <div className="ivcc-result-s">{(v.categories || []).join(", ")}</div>
                  </button>
                ))}
              </>
            )}
            {results.users?.length > 0 && (
              <>
                <div className="ivcc-results-h">People</div>
                {results.users.map((u) => (
                  <button key={u.user_id} type="button" className="ivcc-result" onClick={() => go("/users")}>
                    <div className="ivcc-result-t">{u.name}</div>
                    <div className="ivcc-result-s">{[u.email, u.role?.replace(/_/g, " ")].filter(Boolean).join(" · ")}</div>
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      <div className="ivcc-gap" />

      <div className={`ivcc-live${venueCount ? "" : " idle"}`} data-testid="cc-live">
        <div className="ivcc-live-dot" />
        {venueCount ? `Live · ${venueCount} venue${venueCount === 1 ? "" : "s"}` : "No events today"}
      </div>

      <button type="button" className="ivcc-bell" onClick={() => navigate("/messages")} aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}>
        <Bell strokeWidth={1.8} />
        {unread > 0 && <div className="ivcc-bell-dot" />}
      </button>

      {canCreate && (
        <button type="button" className="ivcc-cta" onClick={() => navigate("/events?new=true")} data-testid="create-event-btn">
          <Plus strokeWidth={2.5} />
          <span>New Event</span>
        </button>
      )}
    </header>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */
export default function DashboardPage() {
  const { authAxios, isAdmin, isSubAdmin, isTeamLead, isVendor } = useAuth();
  const { pendingApprovals = 0, unreadNotifications = 0 } = useNotifications?.() ?? {};
  const navigate = useNavigate();
  const canReview = isAdmin || isSubAdmin || isTeamLead;

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [day, setDay] = useState({ venues: [], vendors: [], stretched_vendors: [], event_count: 0, total_people: 0 });
  const [checkins, setCheckins] = useState({});   // event_id -> {checked_in_people, registered_people, no_show_people}
  const [attendees, setAttendees] = useState({});  // event_id -> [attendee]
  const [meals, setMeals] = useState([]);           // flattened today meals
  const [notifications, setNotifications] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    const api = authAxios();
    const today = format(new Date(), "yyyy-MM-dd");
    const [statsData, dayData, notifData] = await Promise.all([
      safe(api.get("/stats"), {}),
      safe(api.get(`/schedule/day?date=${today}`), null),
      safe(api.get("/notifications"), []),
    ]);
    setStats(statsData || {});
    setNotifications(Array.isArray(notifData) ? notifData : notifData?.notifications || []);

    const d = dayData || {};
    const venues = Array.isArray(d.venues) ? d.venues : [];
    setDay({
      venues,
      vendors: Array.isArray(d.vendors) ? d.vendors : [],
      stretched_vendors: Array.isArray(d.stretched_vendors) ? d.stretched_vendors : [],
      event_count: num(d.event_count),
      total_people: num(d.total_people),
    });

    const events = venues.flatMap((v) => (v.events || []).map((e) => ({ ...e, location: v.location })));
    const perEvent = await Promise.all(events.map(async (e) => {
      const [ci, att, td] = await Promise.all([
        safe(api.get(`/events/${e.event_id}/check-in/stats`), null),
        safe(api.get(`/events/${e.event_id}/attendees`), null),
        safe(api.get(`/events/${e.event_id}/today`), null),
      ]);
      return { e, ci, att, td };
    }));

    const ciMap = {};
    const attMap = {};
    const mealRows = [];
    perEvent.forEach(({ e, ci, att, td }) => {
      if (ci) ciMap[e.event_id] = ci;
      const list = Array.isArray(att) ? att : att?.attendees;
      if (Array.isArray(list)) attMap[e.event_id] = list;
      const todayMeals = td && td.is_event_day !== false && Array.isArray(td.meals) ? td.meals : [];
      todayMeals.forEach((m) => mealRows.push({
        key: `${e.event_id}-${m.meal_id}`,
        type: m.meal_type,
        vendor: m.vendor_name,
        start: m.start_time,
        people: num(e.people),
        event: e.title,
      }));
    });
    mealRows.sort((a, b) => hhmm(a.start).localeCompare(hhmm(b.start)));
    setCheckins(ciMap);
    setAttendees(attMap);
    setMeals(mealRows);
    setUpdatedAt(new Date());
    setLoading(false);
  }, [authAxios]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  /* ── derived ── */
  const events = useMemo(
    () => day.venues.flatMap((v) => (v.events || []).map((e) => ({ ...e, location: v.location }))),
    [day.venues],
  );

  const totals = useMemo(() => {
    let checked = 0; let registered = 0; let noShow = 0;
    events.forEach((e) => {
      const c = checkins[e.event_id];
      if (c) {
        checked += num(c.checked_in_people);
        registered += num(c.registered_people);
        noShow += num(c.no_show_people);
      } else {
        registered += num(e.people);
      }
    });
    if (!registered) registered = day.total_people;
    const expected = Math.max(registered - checked - noShow, 0);
    return { checked, registered, noShow, expected };
  }, [events, checkins, day.total_people]);

  const checkinList = useMemo(() => {
    const titleById = Object.fromEntries(events.map((e) => [e.event_id, e.title]));
    const out = [];
    Object.entries(attendees).forEach(([eventId, list]) => {
      list.forEach((a) => {
        const t = checkinTime(a);
        if (t) out.push({ time: t, name: a.name, event: titleById[eventId] || "" });
      });
    });
    return out.sort((a, b) => b.time - a.time);
  }, [attendees, events]);

  const spark = useMemo(() => sparkPath(checkinList.map((c) => c.time.getTime())), [checkinList]);
  const lastHour = checkinList.filter((c) => Date.now() - c.time.getTime() <= 60 * 60 * 1000).length;
  const last10 = checkinList.filter((c) => Date.now() - c.time.getTime() <= 10 * 60 * 1000).length;

  const needs = useMemo(() => {
    const out = [];
    day.vendors.filter((v) => v.over_capacity).forEach((v) => out.push({
      key: `cap-${v.vendor_id}`, kind: "w",
      text: "Catering count over capacity",
      meta: `${v.vendor_name} · ${Math.max(num(v.people) - num(v.capacity), 0)} over capacity`,
      to: "/day",
    }));
    if (canReview && num(stats.pending_events) > 0) out.push({
      key: "events", kind: "e",
      text: `${num(stats.pending_events)} event${num(stats.pending_events) === 1 ? "" : "s"} awaiting approval`,
      meta: "Approve or send back",
      to: "/approvals",
    });
    if (pendingApprovals > 0) out.push({
      key: "claims", kind: "w",
      text: `${pendingApprovals} claim${pendingApprovals === 1 ? "" : "s"} waiting for review`,
      meta: "Reimbursements",
      to: "/claims",
    });
    if (canReview && num(stats.pending_notes) > 0) out.push({
      key: "notes", kind: "i",
      text: `${num(stats.pending_notes)} change request${num(stats.pending_notes) === 1 ? "" : "s"}`,
      meta: "From event hosts",
      to: "/approvals",
    });
    day.stretched_vendors.forEach((v) => out.push({
      key: `str-${v.vendor_id || v.vendor_name}`, kind: "i",
      text: `${v.vendor_name} is stretched`,
      meta: `${num(v.event_count)} events today`,
      to: "/day",
    }));
    return out;
  }, [day.vendors, day.stretched_vendors, stats, pendingApprovals, canReview]);

  const activity = useMemo(() => {
    const rows = checkinList.slice(0, 8).map((c, i) => ({
      key: `ci-${i}-${c.time.getTime()}`, kind: "chk", time: c.time,
      node: <><strong>{c.name || "Guest"}</strong> checked in{c.event ? ` — ${c.event}` : ""}</>,
    }));
    notifications.slice(0, 12).forEach((n) => {
      const t = toDate(n.created_at);
      const type = String(n.type || "").toLowerCase();
      const kind = /reject|cancel|over|warn|fail|declin/.test(type) ? "alrt"
        : /user|invite|register|attendee|signup/.test(type) ? "usr" : "alrt";
      rows.push({
        key: `n-${n.notif_id}`, kind, time: t,
        node: <><strong>{n.title || "Update"}</strong>{n.message ? ` — ${n.message}` : ""}</>,
        to: n.event_id ? `/events/${n.event_id}` : null,
      });
    });
    return rows
      .sort((a, b) => (b.time?.getTime() || 0) - (a.time?.getTime() || 0))
      .slice(0, 6);
  }, [checkinList, notifications]);

  const upcoming = Array.isArray(stats.upcoming_events) ? stats.upcoming_events.slice(0, 5) : [];
  const locationLabel = day.venues.length === 1 ? day.venues[0].location : "Ismaili Volunteers";
  const newActivity = unreadNotifications + last10;

  /* ── render ── */
  return (
    <div className="ivcc ivcc-page" data-testid="dashboard-page">
      <TopBar
        locationLabel={locationLabel}
        venueCount={day.venues.length}
        unread={unreadNotifications}
        canCreate={!isVendor}
      />

      <div className="ivcc-bento">
        {/* ROW 1 */}
        <div className="ivcc-r1">
          {/* Hero: today's check-ins */}
          <section className="ivcc-card ivcc-stat" data-testid="cc-checkins">
            <div className="ivcc-stat-glow" />
            <div className="ivcc-eye">Today&apos;s check-ins</div>
            <div className="ivcc-hero">
              <div className="ivcc-big">{loading ? "–" : totals.checked}</div>
              <div className="ivcc-of">/<span className="ivcc-ot">{loading ? "–" : totals.registered}</span></div>
            </div>
            {lastHour > 0 ? (
              <div className="ivcc-tag">
                <TrendingUp strokeWidth={2.5} />
                +{lastHour} in the last hour
              </div>
            ) : (
              <div className="ivcc-tag idle">
                <TrendingUp strokeWidth={2.5} />
                {day.event_count
                  ? `${day.event_count} event${day.event_count === 1 ? "" : "s"} · ${day.total_people || totals.registered} expected`
                  : "No events scheduled today"}
              </div>
            )}
            <div className="ivcc-spark" aria-hidden="true">
              <svg viewBox="0 0 100 44" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="ivcc-sg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1b6595" stopOpacity=".3" />
                    <stop offset="100%" stopColor="#1b6595" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d={spark.line} fill="none" stroke="#2480b8" strokeWidth="1.5" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                <path d={spark.area} fill="url(#ivcc-sg)" />
                {checkinList.length > 0 && <circle cx={spark.end[0]} cy={spark.end[1]} r="2.5" fill="#3292cc" />}
              </svg>
            </div>
            <div className="ivcc-track">
              <div className="ivcc-fill" style={{ width: `${pct(totals.checked, totals.registered)}%` }} />
            </div>
            <div className="ivcc-sg">
              <div className="ivcc-sg-item">
                <div className="ivcc-sg-val green">{totals.checked}</div>
                <div className="ivcc-sg-lbl">On site</div>
                <div className="ivcc-sg-bar"><div className="ivcc-sg-fill green" style={{ width: `${pct(totals.checked, totals.registered)}%` }} /></div>
              </div>
              <div className="ivcc-sg-item">
                <div className="ivcc-sg-val warn">{totals.expected}</div>
                <div className="ivcc-sg-lbl">Still expected</div>
                <div className="ivcc-sg-bar"><div className="ivcc-sg-fill warn" style={{ width: `${pct(totals.expected, totals.registered)}%` }} /></div>
              </div>
              <div className="ivcc-sg-item">
                <div className="ivcc-sg-val err">{totals.noShow}</div>
                <div className="ivcc-sg-lbl">No-shows</div>
                <div className="ivcc-sg-bar"><div className="ivcc-sg-fill err" style={{ width: `${pct(totals.noShow, totals.registered)}%` }} /></div>
              </div>
            </div>
          </section>

          {/* Needs you */}
          <section className="ivcc-card ivcc-pad" data-testid="cc-needs">
            <div className="ivcc-head">
              <div className="ivcc-title">Needs you</div>
              <div className={`ivcc-badge${needs.length ? "" : " muted"}`}>{needs.length}</div>
            </div>
            {needs.length === 0 ? (
              <div className="ivcc-empty">{loading ? "Checking…" : "All clear. Nothing needs you right now."}</div>
            ) : needs.slice(0, 3).map((n) => (
              <button key={n.key} type="button" className="ivcc-need" onClick={() => navigate(n.to)}>
                <div className={`ivcc-need-ico ${n.kind}`}>
                  {n.kind === "w" ? <AlertTriangle strokeWidth={2.5} /> : n.kind === "e" ? <AlertCircle strokeWidth={2.5} /> : <Info strokeWidth={2.5} />}
                </div>
                <div>
                  <div className="ivcc-need-text">{n.text}</div>
                  <div className="ivcc-need-meta">{n.meta}</div>
                </div>
                <div className="ivcc-need-arrow"><ChevronRight strokeWidth={2} /></div>
              </button>
            ))}
          </section>

          {/* Vendors on shift */}
          <section className="ivcc-card ivcc-pad" data-testid="cc-vendors">
            <div className="ivcc-head">
              <div className="ivcc-title">Vendors on shift</div>
              <div className="ivcc-badge blue">{day.vendors.length}</div>
            </div>
            {day.vendors.length === 0 ? (
              <div className="ivcc-empty">{loading ? "Loading…" : "No vendors scheduled today."}</div>
            ) : day.vendors.slice(0, 3).map((v, i) => (
              <div key={v.vendor_id || v.vendor_name}>
                {i > 0 && <div className="ivcc-v-div" />}
                <div className="ivcc-v">
                  <div className={`ivcc-v-ava ${i % 2 ? "b" : "a"}`}>{initials(v.vendor_name)}</div>
                  <div className="ivcc-v-inf">
                    <div className="ivcc-v-name" title={v.vendor_name}>{v.vendor_name}</div>
                    <div className={`ivcc-v-status${v.over_capacity ? " over" : ""}`}>
                      <div className="ivcc-v-status-dot" />
                      {v.over_capacity ? "Over capacity" : "On duty"} · {num(v.people)} meals
                    </div>
                  </div>
                  <a
                    className={`ivcc-v-ph${v.contact_phone ? "" : " off"}`}
                    href={v.contact_phone ? `tel:${v.contact_phone}` : undefined}
                    aria-label={v.contact_phone ? `Call ${v.vendor_name}` : "No phone on file"}
                    title={v.contact_phone || "No phone on file"}
                  >
                    <Phone strokeWidth={2} />
                  </a>
                </div>
              </div>
            ))}
            <div className="ivcc-v-div" style={{ marginTop: 8 }} />
            <div className="ivcc-foot">
              {updatedAt ? `Updated ${format(updatedAt, "h:mm a")} · refreshes every 30s` : "Loading…"}
            </div>
          </section>
        </div>

        {/* ROW 2 — venues (or what's coming up when nothing runs today) */}
        <section className="ivcc-card ivcc-clip" data-testid="cc-venues">
          <div className="ivcc-sec-hd">
            <div className="ivcc-sec-title">{events.length || !upcoming.length ? "Today across venues" : "Coming up"}</div>
            <div className="ivcc-cnt">{events.length ? `${events.length} active` : upcoming.length ? `${upcoming.length} upcoming` : "0 active"}</div>
            <button type="button" className="ivcc-more" onClick={() => navigate(events.length ? "/day" : "/events")}>
              View all <ChevronRight strokeWidth={2} />
            </button>
          </div>
          {events.length > 0 ? (
            <div className="ivcc-venues">
              {events.map((e, i) => {
                const c = checkins[e.event_id];
                const got = c ? num(c.checked_in_people) : 0;
                const of = c ? num(c.registered_people) || num(e.people) : num(e.people);
                const p = pct(got, of);
                const color = VENUE_COLORS[i % VENUE_COLORS.length];
                const full = of > 0 && got >= of;
                const level = full || p >= 75 ? "hi" : p >= 40 ? "mid" : "lo";
                return (
                  <button key={e.event_id} type="button" className={`ivcc-vc ${color}`} onClick={() => navigate(`/events/${e.event_id}`)}>
                    <div className="ivcc-vc-loc">{e.location || "Venue"}</div>
                    <div className="ivcc-vc-name">{e.title}</div>
                    <div className="ivcc-vc-time">
                      {e.first_meal ? `${hhmm(e.first_meal)}${e.last_meal ? ` – ${hhmm(e.last_meal)}` : ""}` : "All day"}
                    </div>
                    <div className="ivcc-vc-metric">
                      <div className="ivcc-vc-num">{got}</div>
                      <div className="ivcc-vc-denom">/ {of}</div>
                      <div className={`ivcc-vc-pct ${level}`}>{full ? "Full" : `${p}%`}</div>
                    </div>
                    <div className="ivcc-vc-prog"><div className={`ivcc-vf ${color}`} style={{ width: `${p}%` }} /></div>
                    {e.status === "pending" && <div className="ivcc-vc-note">Awaiting approval</div>}
                  </button>
                );
              })}
            </div>
          ) : upcoming.length > 0 ? (
            <div className="ivcc-venues">
              {upcoming.map((e, i) => {
                const color = VENUE_COLORS[i % VENUE_COLORS.length];
                const d = e.start_date ? new Date(`${e.start_date}T00:00:00`) : null;
                const days = d ? Math.round((d - new Date(new Date().toDateString())) / 86400000) : null;
                return (
                  <button key={e.event_id} type="button" className={`ivcc-vc ${color}`} onClick={() => navigate(`/events/${e.event_id}`)}>
                    <div className="ivcc-vc-loc">{e.location || "Venue to be set"}</div>
                    <div className="ivcc-vc-name">{e.title}</div>
                    <div className="ivcc-vc-time">{d ? format(d, "EEE, MMM d") : "Date to be set"}</div>
                    <div className="ivcc-vc-metric">
                      <div className="ivcc-vc-num">{days != null && days >= 0 ? days : "–"}</div>
                      <div className="ivcc-vc-denom">{days === 1 ? "day to go" : "days to go"}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="ivcc-venues-empty">
              {loading ? "Loading today’s schedule…" : "No events are scheduled today."}
              {!loading && (
                <button type="button" className="ivcc-more" style={{ marginLeft: 0 }} onClick={() => navigate("/events")}>
                  Open events <ChevronRight strokeWidth={2} />
                </button>
              )}
            </div>
          )}
        </section>

        {/* ROW 3 */}
        <div className="ivcc-r3">
          <section className="ivcc-card ivcc-clip" data-testid="cc-activity">
            <div className="ivcc-sec-hd">
              <div className="ivcc-title">Live activity</div>
              {newActivity > 0 && <div className="ivcc-badge blue">{newActivity} new</div>}
            </div>
            {activity.length === 0 ? (
              <div className="ivcc-act-empty">{loading ? "Loading…" : "Check-ins and updates will appear here as they happen."}</div>
            ) : (
              <div>
                {activity.map((a) => (
                  <div
                    key={a.key}
                    className="ivcc-act-row"
                    role={a.to ? "button" : undefined}
                    tabIndex={a.to ? 0 : undefined}
                    style={a.to ? { cursor: "pointer" } : undefined}
                    onClick={a.to ? () => navigate(a.to) : undefined}
                    onKeyDown={a.to ? (ev) => { if (ev.key === "Enter") navigate(a.to); } : undefined}
                  >
                    <div className={`ivcc-act-ico ${a.kind}`}>
                      {a.kind === "chk" ? <Check strokeWidth={2} /> : a.kind === "usr" ? <Users strokeWidth={2} /> : <AlertTriangle strokeWidth={2} />}
                    </div>
                    <div className="ivcc-act-text">{a.node}</div>
                    <div className="ivcc-act-time">{ago(a.time)}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="ivcc-card ivcc-clip" data-testid="cc-meals">
            <div className="ivcc-meal-hd">
              <div className="ivcc-title">Meal plan · Today</div>
              <div className="ivcc-badge blue">{meals.length} item{meals.length === 1 ? "" : "s"}</div>
            </div>
            <div className="ivcc-meal-list">
              {meals.length === 0 ? (
                <div className="ivcc-empty">{loading ? "Loading…" : "No meals scheduled today."}</div>
              ) : meals.slice(0, 8).map((m, i) => (
                <div key={m.key} className="ivcc-meal" title={[m.event, m.vendor].filter(Boolean).join(" · ")}>
                  <div className="ivcc-meal-dot" style={{ background: MEAL_COLORS[i % MEAL_COLORS.length] }} />
                  <div className="ivcc-meal-nm">
                    {m.type ? m.type.charAt(0).toUpperCase() + m.type.slice(1) : "Meal"}
                    {m.vendor && <span className="ivcc-meal-sub"> · {m.vendor}</span>}
                  </div>
                  <div className="ivcc-meal-cnt">{m.people}</div>
                  <div className="ivcc-meal-time">{hhmm(m.start)}</div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
